'use client';

import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Loader2, Check, ArrowLeft, Calendar, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AlertCircle } from 'lucide-react';
import WhyBookSelector from './WhyBookSelector';
import DateSlotPicker from './DateSlotPicker';
import { getTimezoneAbbr, convertTimeToTimezone, parseDateTimeInTimezone, formatCalendarTimestamp } from '@/lib/utils/timezone';
import type { GuideProfile, BookingType, WhyBookIntent } from '@/lib/services/guide-profile-service';

interface BookingCardProps {
  guideProfile: GuideProfile;
  guideUserId: string;
  guideName: string;
  guideHandle: string;
  domainId?: string;
  domainHandle?: string;
}

type Step = 'intent' | 'date' | 'details' | 'confirm' | 'success';

function formatPrice(bt: BookingType) {
  if (bt.is_free) return 'Free';
  return `$${(bt.price_cents / 100).toFixed(0)}`;
}

function formatTime(t: string) {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function BookingCard({ guideProfile, guideUserId, guideName, guideHandle, domainId, domainHandle }: BookingCardProps) {
  const [hasAvailability, setHasAvailability] = useState<boolean | null>(null);
  const [step, setStep] = useState<Step>('intent');
  const [selectedIntentId, setSelectedIntentId] = useState<string | null>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string>(
    guideProfile.booking_types[0]?.id || ''
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(null);
  const [selectedEndTime, setSelectedEndTime] = useState<string | null>(null);
  const [guideTimezone, setGuideTimezone] = useState('America/New_York');
  const [guestTimezone, setGuestTimezone] = useState<string>('');
  const [topic, setTopic] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const detectedGuestTz = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );
  const effectiveGuestTz = guestTimezone || detectedGuestTz;
  const showDualTz = effectiveGuestTz !== guideTimezone;

  const selectedBooking = guideProfile.booking_types.find((bt) => bt.id === selectedBookingId);
  const selectedIntent = guideProfile.why_book_intents.find((i) => i.id === selectedIntentId);

  useEffect(() => {
    const now = new Date();
    const start = now.toISOString().substring(0, 10);
    const end = new Date(now.getTime() + 28 * 24 * 60 * 60 * 1000)
      .toISOString()
      .substring(0, 10);

    fetch(
      `/api/guide/availability/${guideHandle}?start=${start}&end=${end}&duration=${selectedBooking?.duration_minutes || 30}`
    )
      .then((res) => res.json())
      .then((data) => {
        setHasAvailability(data.slots && data.slots.length > 0);
      })
      .catch(() => setHasAvailability(false));
  }, [guideHandle, selectedBooking?.duration_minutes]);

  async function handleRequestSubmit() {
    if (!selectedBooking || !guestName || !guestEmail) return;
    if (!isValidEmail(guestEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError(null);
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch('/api/guide/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guideUserId,
          guestName,
          guestEmail,
          intentId: selectedIntentId || 'explore',
          intentLabel: selectedIntent?.label || 'Just Explore',
          bookingTypeId: selectedBooking.id,
          bookingLabel: selectedBooking.label,
          durationMinutes: selectedBooking.duration_minutes,
          topic,
        }),
      });

      if (res.ok) {
        setStep('success');
      } else {
        const data = await res.json().catch(() => null);
        setSubmitError(data?.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const isPaidBooking = selectedBooking && selectedBooking.price_cents > 0;
  const guideStripeReady = guideProfile.stripe_connect_onboarded;

  async function handleBookingSubmit() {
    if (!selectedBooking || !guestName || !guestEmail || !selectedDate || !selectedStartTime) return;
    if (!isValidEmail(guestEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError(null);
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const scheduledAt = `${selectedDate}T${selectedStartTime}:00`;
      const payload = {
        guideUserId,
        guestName,
        guestEmail,
        bookingTypeId: selectedBooking.id,
        durationMinutes: selectedBooking.duration_minutes,
        priceCents: selectedBooking.price_cents,
        currency: selectedBooking.currency,
        intentId: selectedIntentId || null,
        intentLabel: selectedIntent?.label || null,
        topic: topic || null,
        scheduledAt,
        timezone: guideTimezone,
        guestTimezone: effectiveGuestTz,
        ...(domainId && { domainId }),
      };

      if (isPaidBooking) {
        const res = await fetch('/api/guide/bookings/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (res.ok) {
          const data = await res.json();
          if (data.checkout_url) {
            window.location.href = data.checkout_url;
            return;
          }
          if (data.booking) {
            setJoinUrl(data.booking.join_url || null);
            setStep('success');
          }
        } else {
          const data = await res.json().catch(() => null);
          setSubmitError(data?.error || 'This time slot may no longer be available. Please try another time.');
        }
      } else {
        const res = await fetch('/api/guide/bookings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (res.ok) {
          const data = await res.json();
          setJoinUrl(data.data?.join_url || null);
          setStep('success');
        } else {
          const data = await res.json().catch(() => null);
          setSubmitError(data?.error || 'This time slot may no longer be available. Please try another time.');
        }
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function renderTimeDisplay(time: string) {
    const guideAbbr = getTimezoneAbbr(guideTimezone);
    const guideDisplay = `${formatTime(time)} ${guideAbbr}`;
    if (!showDualTz || !selectedDate) return guideDisplay;
    const guestTime = convertTimeToTimezone(time, selectedDate, guideTimezone, effectiveGuestTz);
    const guestAbbr = getTimezoneAbbr(effectiveGuestTz);
    return (
      <>
        {guestTime} {guestAbbr}
        <span className="text-muted-foreground text-xs ml-1">
          ({guideDisplay})
        </span>
      </>
    );
  }

  if (hasAvailability === null) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!hasAvailability) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Book a Conversation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <WhyBookSelector
            intents={guideProfile.why_book_intents}
            selectedId={selectedIntentId}
            onSelect={setSelectedIntentId}
          />
          <Separator />
          <div className="space-y-2">
            <p className="text-sm font-medium">Select session</p>
            <div className="space-y-2">
              {guideProfile.booking_types.map((bt) => (
                <button
                  key={bt.id}
                  onClick={() => setSelectedBookingId(bt.id)}
                  className={cn(
                    'w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors',
                    selectedBookingId === bt.id
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{bt.label}</span>
                    <span className="font-semibold">{formatPrice(bt)}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{bt.duration_minutes} minutes</span>
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm">What would you like to discuss?</Label>
            <Textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Brief description of what you'd like to talk about..."
              rows={3}
            />
          </div>
          <Separator />
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-sm">Your name</Label>
              <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Your full name" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Your email</Label>
              <Input type="email" value={guestEmail} onChange={(e) => { setGuestEmail(e.target.value); setEmailError(null); }} placeholder="you@example.com" />
              {emailError && <p className="text-xs text-destructive">{emailError}</p>}
            </div>
          </div>
          {selectedBooking && (
            <>
              <Separator />
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {selectedBooking.label} &middot; {selectedBooking.duration_minutes} min
                </span>
                <span className="font-semibold">{formatPrice(selectedBooking)}</span>
              </div>
            </>
          )}
          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}
          <Button className="w-full" size="lg" onClick={handleRequestSubmit} disabled={isSubmitting || !guestName || !guestEmail}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Request a Conversation
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (step === 'success') {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <Check className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">Booking Confirmed!</h3>
            {selectedDate && selectedStartTime && (
              <p className="text-sm text-muted-foreground">
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}{' '}
                at {renderTimeDisplay(selectedStartTime)}
              </p>
            )}
            <p className="text-sm text-muted-foreground mt-1">
              A confirmation email has been sent to {guestEmail}.
            </p>
          </div>
          {selectedDate && selectedStartTime && selectedBooking && (
            <div className="space-y-3">
              <p className="text-xs font-medium text-muted-foreground">Add to your calendar</p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                {(() => {
                  const start = parseDateTimeInTimezone(`${selectedDate}T${selectedStartTime}:00`, guideTimezone);
                  const end = new Date(start.getTime() + (selectedBooking.duration_minutes * 60 * 1000));
                  const fmt = (d: Date) => formatCalendarTimestamp(d);
                  const title = encodeURIComponent(`Conversation with ${guideName}`);
                  const details = encodeURIComponent(`Join: ${joinUrl || ''}`);
                  const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}&location=${encodeURIComponent(joinUrl || '')}`;
                  const outlookUrl = `https://outlook.live.com/calendar/0/action/compose?subject=${title}&startdt=${start.toISOString()}&enddt=${end.toISOString()}&body=${details}&location=${encodeURIComponent(joinUrl || '')}`;
                  const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:Conversation with ${guideName}\nDTSTART:${fmt(start)}\nDTEND:${fmt(end)}\nURL:${joinUrl || ''}\nDESCRIPTION:Join link: ${joinUrl || ''}\nEND:VEVENT\nEND:VCALENDAR`;
                  const icsBlob = new Blob([icsContent], { type: 'text/calendar' });
                  const icsUrl = URL.createObjectURL(icsBlob);
                  return (
                    <>
                      <Button variant="outline" size="sm" asChild>
                        <a href={gcalUrl} target="_blank" rel="noopener noreferrer">
                          <Calendar className="w-4 h-4 mr-1" /> Google
                        </a>
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <a href={outlookUrl} target="_blank" rel="noopener noreferrer">
                          <Calendar className="w-4 h-4 mr-1" /> Outlook
                        </a>
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <a href={icsUrl} download={`booking-${guideName.replace(/\s/g, '-')}.ics`}>
                          <Calendar className="w-4 h-4 mr-1" /> .ics
                        </a>
                      </Button>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          {step !== 'intent' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => {
                setSubmitError(null);
                if (step === 'date') setStep('intent');
                if (step === 'details') setStep('date');
                if (step === 'confirm') setStep('details');
              }}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <CardTitle className="text-lg">Book a Conversation</CardTitle>
        </div>
        <div className="flex gap-1 mt-2">
          {(['intent', 'date', 'details', 'confirm'] as Step[]).map((s) => (
            <div
              key={s}
              className={cn(
                'h-1 flex-1 rounded-full',
                s === step ? 'bg-primary' :
                (['intent', 'date', 'details', 'confirm'].indexOf(s) <
                  ['intent', 'date', 'details', 'confirm'].indexOf(step))
                  ? 'bg-primary/40'
                  : 'bg-muted'
              )}
            />
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {step === 'intent' && (
          <>
            <WhyBookSelector
              intents={guideProfile.why_book_intents}
              selectedId={selectedIntentId}
              onSelect={setSelectedIntentId}
            />
            <Separator />
            <div className="space-y-2">
              <p className="text-sm font-medium">Select session</p>
              <div className="space-y-2">
                {guideProfile.booking_types.map((bt) => (
                  <button
                    key={bt.id}
                    onClick={() => setSelectedBookingId(bt.id)}
                    className={cn(
                      'w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors',
                      selectedBookingId === bt.id
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-primary/50'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{bt.label}</span>
                      <span className="font-semibold">{formatPrice(bt)}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{bt.duration_minutes} minutes</span>
                  </button>
                ))}
              </div>
            </div>
            <Button
              className="w-full"
              onClick={() => setStep('date')}
              disabled={!selectedBookingId}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Choose Date & Time
            </Button>
          </>
        )}

        {step === 'date' && selectedBooking && (
          <>
            <DateSlotPicker
              handle={guideHandle}
              durationMinutes={selectedBooking.duration_minutes}
              selectedDate={selectedDate || undefined}
              selectedTime={selectedStartTime || undefined}
              onSelect={(date, start, end, tz, gtz) => {
                setSelectedDate(date);
                setSelectedStartTime(start);
                setSelectedEndTime(end);
                setGuideTimezone(tz);
                setGuestTimezone(gtz);
              }}
            />
            <Button
              className="w-full"
              onClick={() => setStep('details')}
              disabled={!selectedDate || !selectedStartTime}
            >
              Continue
            </Button>
          </>
        )}

        {step === 'details' && (
          <>
            <div className="space-y-2">
              <Label className="text-sm">What would you like to discuss?</Label>
              <Textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Brief description..."
                rows={3}
              />
            </div>
            <Separator />
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-sm">Your name</Label>
                <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Your full name" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Your email</Label>
                <Input type="email" value={guestEmail} onChange={(e) => { setGuestEmail(e.target.value); setEmailError(null); }} placeholder="you@example.com" />
                {emailError && <p className="text-xs text-destructive">{emailError}</p>}
              </div>
            </div>
            <Button
              className="w-full"
              onClick={() => {
                if (!isValidEmail(guestEmail)) {
                  setEmailError('Please enter a valid email address');
                  return;
                }
                setEmailError(null);
                setStep('confirm');
              }}
              disabled={!guestName || !guestEmail}
            >
              Review Booking
            </Button>
          </>
        )}

        {step === 'confirm' && selectedBooking && selectedDate && selectedStartTime && (
          <>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Session</span>
                <span className="font-medium">{selectedBooking.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date</span>
                <span className="font-medium">
                  {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-muted-foreground">Time</span>
                <span className="font-medium text-right">
                  {renderTimeDisplay(selectedStartTime)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration</span>
                <span className="font-medium">{selectedBooking.duration_minutes} min</span>
              </div>
              {selectedIntent && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Purpose</span>
                  <span className="font-medium">{selectedIntent.label}</span>
                </div>
              )}
              {topic && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Topic</span>
                  <span className="font-medium truncate max-w-[200px]">{topic}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Guest</span>
                <span className="font-medium">{guestName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium">{guestEmail}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-semibold">
                <span>Price</span>
                <span>{formatPrice(selectedBooking)}</span>
              </div>
            </div>
            {isPaidBooking && !guideStripeReady && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>This guide hasn&apos;t set up payments yet. Please try again later or choose a free session.</span>
              </div>
            )}
            {submitError && (
              <p className="text-sm text-destructive">{submitError}</p>
            )}
            <Button
              className="w-full"
              size="lg"
              onClick={handleBookingSubmit}
              disabled={isSubmitting || (isPaidBooking && !guideStripeReady)}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isPaidBooking ? 'Proceed to Payment' : 'Confirm Booking'}
            </Button>
            {isPaidBooking && guideStripeReady && (
              <p className="text-xs text-center text-muted-foreground">
                You&apos;ll be redirected to Stripe to complete payment
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
